// Theme state — the `next-themes` replacement (ADR-017).
//
// next-themes did four things: persist the choice, resolve `system` against
// `prefers-color-scheme`, toggle a class on <html>, and run a pre-paint script
// so the page never flashes the wrong theme. The first three are below; the
// fourth is the inline script in `index.html`, which must stay in sync with
// STORAGE_KEY and the values here.
//
// The exported hook keeps next-themes' `{ theme, setTheme }` shape so call
// sites did not change during the migration.

import * as React from "react";

const STORAGE_KEY = "aviary-admin-theme";

export type Theme = "light" | "dark" | "system";

interface ThemeContextValue {
  /** The stored preference — may be "system". */
  theme: Theme;
  /** What "system" currently resolves to. Never "system". */
  resolvedTheme: "light" | "dark";
  setTheme: (theme: Theme) => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

function prefersDark(): boolean {
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolve(theme: Theme): "light" | "dark" {
  if (theme !== "system") return theme;
  return prefersDark() ? "dark" : "light";
}

function readStored(defaultTheme: Theme): Theme {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "light" || raw === "dark" || raw === "system") return raw;
  } catch {
    /* private mode / storage disabled — fall through to the default */
  }
  return defaultTheme;
}

export function ThemeProvider({
  children,
  defaultTheme = "dark",
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
}) {
  const [theme, setThemeState] = React.useState<Theme>(() => readStored(defaultTheme));
  const [resolvedTheme, setResolvedTheme] = React.useState<"light" | "dark">(() => resolve(theme));

  // Apply to <html> and persist. Tailwind's darkMode is class-based, so the
  // class on the root element is what every `dark:` utility keys off.
  React.useEffect(() => {
    const next = resolve(theme);
    setResolvedTheme(next);
    document.documentElement.classList.toggle("dark", next === "dark");
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* best effort — the class above still applied for this session */
    }
  }, [theme]);

  // Follow the OS while the preference is "system". Not subscribing would
  // leave a "system" user stale until reload.
  React.useEffect(() => {
    if (theme !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      const next = prefersDark() ? "dark" : "light";
      setResolvedTheme(next);
      document.documentElement.classList.toggle("dark", next === "dark");
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const value = React.useMemo<ThemeContextValue>(
    () => ({ theme, resolvedTheme, setTheme: setThemeState }),
    [theme, resolvedTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
